<template>
  <v-container id="users_root" fluid>
    <!-- DIALOG -->
    <router-view name="dialog"></router-view>
    <!-- CONTENT -->
    <v-row class="mb-6 px-6" align="center" justify="center">
      <v-col cols="7">
        <span class="text-h5">
          {{ $t('users.title.list') }}
        </span>
      </v-col>

      <v-col class="d-flex flex-row align-self-end flexendforced" align="sretch">
        <v-btn id="create_user" class="align-self-end mr-8" color="primary" align="right" to="/users/add">
          <v-icon left>mdi-plus-circle-outline</v-icon>
          <span class="ml-2 text-capitalize">{{ $t('users.content.createUser') }}</span>
        </v-btn>

        <v-text-field
          id="user_search"
          v-model="search"
          append-icon="mdi-magnify"
          :label="$t('dataTable.search')"
          single-line
          hide-details
          clearable
          align="right"
        >
        </v-text-field>
      </v-col>
    </v-row>

    <v-card class="pa-5">
      <v-data-table
        must-sort
        :headers="headers"
        :items="userList"
        :options.sync="pagination"
        :server-items-length="totalItems"
        :loading="dataTableLoading"
        :loading-text="$t('dataTable.content.loadingPleaseWait')"
        :no-data-text="$t('dataTable.content.noDataAvailable')"
        :footer-props="{
          'items-per-page-text': $t('dataTable.footer.itemsPerPageText'),
          'page-text': $t('dataTable.footer.pageText'),
          'items-per-page-options': [10,25,50]
        }"
      >
        <template #[`item.name`]="{item}">
          <span>{{ item.firstName }} {{ item.lastName }} </span>
        </template>

        <template #[`item.status`]="{ item }">
          <v-chip label :color="getChipStyle(item)">
            {{ getChipText(item) }}
          </v-chip>
        </template>

        <template #[`item.role`]="{ item }">
          <span>{{ getRoleChipText(item) }}</span>
        </template>

        <template #[`item.createdAt`]="{ item }">
          <span class="font-italic caption">{{ convertDate( item.createdAt ) }}</span>
        </template>

        <template #[`item.actions`]="{ item }">
          <v-btn class="pa-1 " text :to="'credentials/' + item.id + '/add'">
            <v-icon>mdi-plus-circle-outline</v-icon>
            {{ $t('dataTable.content.action.users.createCredential') }}
          </v-btn>
          <v-btn id="edit_user" class="pa-1 " text :to="'/users/' + item.id + '/update'">
            <v-icon>mdi-cog-outline</v-icon>
            {{ $t('dataTable.content.action.edit') }}
          </v-btn>
          <v-btn id="delete_user" class="pa-1 " text @click="deleteUser(item.id, $event)">
            <v-icon>mdi-delete</v-icon>
            {{ $t('dataTable.content.action.delete') }}
          </v-btn>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import Role from '../../Role'
import users from '../../services/api/users'
import debounce from 'lodash.debounce'
import CommonError from '../CommonError'
import { showToastSuccess, showToastError, showConfirmationMenu } from '../../event-bus'

export default {
  name: 'Users',

  data() {
    return {
      search: null,
      pagination: {
        sortBy: ['name']
      },
      userList: [],
      totalItems: 0,
      dataTableLoading: false,
      headers: [
        {
          text: this.$t('dataTable.header.users.id'),
          id: 'userId',
          align: 'left',
          value: 'id'
        },
        {
          text: this.$t('dataTable.header.users.name'),
          id: 'username',
          align: 'left',
          value: 'name'
        },
        {
          text: this.$t('dataTable.header.users.status'),
          id: 'status',
          align: 'left',
          value: 'status'
        },
        {
          text: this.$t('dataTable.header.users.role'),
          id: 'role',
          align: 'left',
          value: 'role'
        },
        {
          text: this.$t('dataTable.header.createdAt'),
          id: 'createdAt',
          align: 'left',
          value: 'createdAt'
        },
        {
          text: this.$t('dataTable.header.users.email'),
          id: 'email',
          align: 'left',
          value: 'email'
        },
        {
          text: this.$t('dataTable.header.actions'),
          id: 'acitions',
          align: 'right',
          value: 'actions',
          sortable: false
        }
      ]
    }
  },

  computed: {
    ...mapGetters(['hasRole']),
    isAdmin() {
      return this.hasRole(Role.ADMIN)
    }
  },

  watch: {
    search() {
      this.dataTableLoading = true
      this.searchUserWatcher()
    },

    pagination() {
      this.searchUsers()
    }
  },

  methods: {
    async searchUsers() {
      this.dataTableLoading = true

      const pageRequest = {
        'page-num': this.pagination.page,
        'page-size': this.pagination.itemsPerPage || 10,
        'sort-field': this.pagination.sortBy[0],
        'sort-order': this.pagination.sortDesc[0] ? 'desc' : 'asc'
      }

      try {
        const page = await users.listUsers(this.search, pageRequest)
        this.userList = page.content
        this.totalItems = page.total
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      } finally {
        this.dataTableLoading = false
      }
    },

    searchUserWatcher: debounce(function() {
      this.searchUsers()
    }, 500),

    async deleteUser(id, e) {
      const conf = await showConfirmationMenu({ title: this.$t('dialog.confirmation.title'), posX: e.clientX, posY: e.clientY })
      if (!conf) {
        return
      }

      try {
        await users.deleteUser(id)
        this.searchUsers()
        showToastSuccess(this.$t('toastMessage.succesfullyDeleted'))
      } catch (error) {
        let errorMsg = this.$t('toastMessage.errorWhileRetrievingData')
        if ((error.response !== undefined) && (error.response.data.error !== undefined)) {
          const splittedErrorMessage = error.response.data.error_description.split(' ')
          if (error.response.data.error === CommonError.STILL_REFERENCED) {
            errorMsg = this.$t('toastMessage.stillReferenced', { 0: splittedErrorMessage[0], 1: splittedErrorMessage[5] })
          } else {
            errorMsg = this.getErrorMessage(error)
          }
        } else {
          errorMsg = this.getErrorMessage(error)
        }
        showToastError(errorMsg)
      }
    },

    userIsAdmin(user) {
      return user.roles.includes(Role.ADMIN)
    },

    getChipStyle(item) {
      if (item.status === 'ENABLED') {
        return 'statusEnabled'
      } else {
        return 'statusInactive'
      }
    },

    getChipText(item) {
      if (item.status === 'ENABLED') {
        return this.$t('dataTable.header.users.states.enabled')
      } else {
        return this.$t('dataTable.header.users.states.inactive')
      }
    },

    getRoleChipText(item) {
      if (this.userIsAdmin(item)) {
        return this.$t('dataTable.header.users.roles.admin')
      } else {
        return this.$t('dataTable.header.users.roles.user')
      }
    }
  }
}
</script>

<style>
</style>
